import MainModel from "../../components/Models/MainModel";
import { motion } from "framer-motion";
import Image from "../../components/images/Image";
import Button from "../../components/buttons/Button";
import { getFirefoxCssFix } from "../../components/commonfunctions/getFirefoxCssFix";
import moment from "moment";

interface TransactionDetails {
  [key: string]: string | number; // Allows multiple key-value pairs
}

interface TransactionReceiptProps {
  details: TransactionDetails;
  handleClickDone: () => void;
  defaultPaymentType?: string;
  headerMessage?: string;
  bulkTransfer?:boolean;
  customWidth?:string;
  customHeight?:string;
  handleCancel?: () => void;
  handleGoToDashboard?:() => void;
}

export default function TransactionReceipt({
  details,
  handleClickDone,
  defaultPaymentType,
  headerMessage = "E Top Up Requested Successfully",
  bulkTransfer,
  customWidth,
  customHeight,
  handleCancel,
  handleGoToDashboard,
}: TransactionReceiptProps) {
  const imageVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };
  const headingVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  const transactionDetails = defaultPaymentType === 'paid' ? [
    "Date",
    "Transaction ID",
    "Type of Transaction",
    "Advance Amount",
    "Payable Amount",
    "Paid Amount",
    "Parent User Code",
    "Requester User Code",
    "Wallet Type",
    "Parent Location",
    "Requester Location",
  ] : defaultPaymentType === 'stock_transfer' ? [
    "Date",
    "Transaction ID",
    "Type of Transaction",
    "Amount",
    "Sender",
    "Receiver",
  ] : [
    "Date",
    "Transaction ID",
    "Type of Transaction",
    "Amount",
    "Parent User Code",
    "Requester User Code",
    "Wallet Type",
    "Parent Location",
    "Requester Location",
  ];

  const getDisplayValue = (label: string) => {
    switch (label) {
      case "Date":
        return  moment(details["date"]).format(
                      "DD-MM-YYYY, hh:mm A"
                    );
      case "Amount":
      case "Paid Amount":
      case "Advance Amount":
      case "Payable Amount":
        // For stock transfer, amount is already formatted
        if (defaultPaymentType === 'stock_transfer' && typeof details[label.toLowerCase().replaceAll(" ", "_")] === 'string') {
          return details[label.toLowerCase().replaceAll(" ", "_")];
        }
        return `AFA ${Number(details[label.toLowerCase().replaceAll(" ", "_")]).toFixed(2)}`;
      case "Sender":
        return details["sender"] ?? "-";
      case "Receiver":
        return details["receiver"] ?? "-";
      default:
        return details[label.toLowerCase().replaceAll(" ", "_")] ?? "-";
    }
  };
   if (bulkTransfer) {
    return (
      <MainModel
        handleClose={handleClickDone}
        modelWidth={customWidth}
        modelHeight={customHeight}
      >
        <motion.div
          variants={imageVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.4 }}
          className="flex items-center justify-center"
        >
          {/* Green checkmark icon */}
          <motion.div
            variants={imageVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.4 }}
            className="flex items-center justify-center"
          >
            <Image src={"success_1"} className="" />
          </motion.div>
        </motion.div>

        <motion.h1
          variants={headingVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.5, ease: "easeInOut", delay: 0.2 }}
          className="text-primary_text text-heading3 text-center pt-6 font-semibold"
        >
          {headerMessage}
        </motion.h1>

        <div className="mt-6 space-y-2">
          <p className="text-gray-500 text-base">{details?.description}</p>
          <p className="text-gray-500 text-base">You can view the status in your Transaction History.</p>
        </div>

        <div className="mt-8 flex gap-3">
          <Button
            buttonName={"Cancel"}
            textSize={"text-large"}
            buttonOnclick={handleCancel || handleClickDone}
            testId={`cancel`}
            loading={false}
            buttonWidth={"flex-1 cursor-pointer"}
            buttonHight="h-[42px]"
            bgColor="bg-white"
            textColor="text-p_primary border border-p_primary max-w-[100px]"

          />
          <Button
            buttonName={"Go to Dashboard"}
            textSize={"text-large"}
            buttonOnclick={handleGoToDashboard || handleClickDone}
            testId={`go-to-dashboard`}
            loading={false}
            buttonWidth={"w-[154px]"}
            buttonHight="h-[42px]"
            bgColor="bg-p_primary"
            textColor="text-white max-w-[200px] rounded-[8px]"
          />
        </div>
      </MainModel>
    )
  }
  return (
    <MainModel handleClose={handleClickDone}>
      <motion.div
        variants={imageVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.4 }}
        className="flex items-center justify-center"
      >
        <Image src={"success_1"} className="" />
      </motion.div>

      <motion.h1
        variants={headingVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5, ease: "easeInOut", delay: 0.2 }}
        className="text-primary_text text-heading3 text-center pt-6"
      >
        {headerMessage}
      </motion.h1>

      <div className="h-transactionPopupHight">
        <div
          className={`h-full border mt-8 rounded-lg overflow-x-hidden overflow-auto ${getFirefoxCssFix(
            "scroll"
          )}`}
        >
          {transactionDetails.map((label, index) => (
            <div key={label} className={`flex justify-between p-4 ${index < transactionDetails.length - 1 ? 'border-b' : ''}`}>
              <p className="text-title2 min-w-[140px] shrink-0 pr-2">{label}</p>
              <p className="text-title1 break-all text-right">{getDisplayValue(label)}</p>
              {/* <p className="text-title2">{label}</p>
              <p className="text-title1">{getDisplayValue(label)}</p> */}
              {/* <p className="text-title2 min-w-[50%] whitespace-nowrap">{label}</p>
              <p className="text-title1 text-title1 break-all w-1/2">{getDisplayValue(label)}</p> */}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <Button
          buttonName={"Done"}
          className={`py-2.5 rounded-[6px] cursor-pointer`}
          textSize={"text-large"}
          buttonOnclick={handleClickDone}
          testId={`done`}
          loading={false}
          buttonWidth={"w-[100px] cursor-pointer"}
          buttonHight="h-[42px]"
        />
      </div>
    </MainModel>
  );
}
